/**
 * Studio lighting — a faithful reproduction of model-viewer @3.5.0's look.
 *
 * model-viewer's image is NOT a 3-point directional rig. It is:
 *   1. A procedurally-baked "legacy" room environment (EnvironmentScene), baked
 *      through PMREM. This carries the warm, even, low-contrast cast that the
 *      reference shows — RoomEnvironment alone is cooler/flatter and cannot match
 *      it, and a hand-tuned key/fill/rim rig fights it. So: pure IBL, no rig.
 *   2. ACESFilmicToneMapping (set in Renderer.ts) — the warm filmic shoulder.
 *   3. A soft studio CONTACT shadow on the floor (ContactShadow class below).
 *
 * Brightness note: three's PMREMGenerator.fromScene(legacyEnv, 0.04) lands a bit
 * brighter than model-viewer's real SH/cube-blur path, so the per-product
 * environmentIntensity carries a brightness compensation (~0.72 for Bangalow)
 * while exposure stays at the prod-faithful 1.0.
 */

import * as THREE from "three";
import { HorizontalBlurShader } from "three/examples/jsm/shaders/HorizontalBlurShader.js";
import { VerticalBlurShader } from "three/examples/jsm/shaders/VerticalBlurShader.js";
import type { DeviceProfile } from "@/types";

export interface LightingOptions {
  /** scene.environmentIntensity. Default 0.72 (the measured model-viewer match;
   *  compensates for three's PMREM being brighter than model-viewer's dist). */
  environmentIntensity?: number;
}

const DEFAULT_ENV_INTENSITY = 0.72;

/**
 * model-viewer @3.5.0 EnvironmentScene('legacy') — reconstructed verbatim from
 * the source. One BackSide room box + one PointLight + 6 emissive area boxes
 * (a MeshBasicMaterial whose colour scalar == the light intensity, so it blows
 * out into the IBL). Baked through PMREM in setupLighting.
 */
function createAreaLightMaterial(intensity: number): THREE.MeshBasicMaterial {
  const mat = new THREE.MeshBasicMaterial();
  mat.color.setScalar(intensity);
  return mat;
}

function buildLegacyEnvironmentScene(): THREE.Scene {
  const envScene = new THREE.Scene();
  envScene.position.y = -3.5;

  const geometry = new THREE.BoxGeometry();
  geometry.deleteAttribute("uv");
  const roomMaterial = new THREE.MeshStandardMaterial({ metalness: 0, side: THREE.BackSide });
  const room = new THREE.Mesh(geometry, roomMaterial);
  room.position.set(-0.757, 13.219, 0.717);
  room.scale.set(31.713, 28.305, 28.591);
  envScene.add(room);

  const mainLight = new THREE.PointLight(0xffffff, 500, 28, 2);
  mainLight.position.set(0.418, 16.199, 0.3);
  envScene.add(mainLight);

  const specs: Array<[number, [number, number, number], [number, number, number]]> = [
    [50, [-16.116, 14.37, 8.208], [0.1, 2.428, 2.739]],
    [50, [-16.109, 18.021, -8.207], [0.1, 2.425, 2.751]],
    [17, [14.904, 12.198, -1.832], [0.15, 4.265, 6.331]],
    [43, [-0.462, 8.89, 14.52], [4.38, 5.441, 0.088]],
    [20, [3.235, 11.486, -12.541], [2.5, 2.0, 0.1]],
    [100, [0.0, 20.0, 0.0], [1.0, 0.1, 1.0]],
  ];
  for (const [intensity, pos, scale] of specs) {
    const light = new THREE.Mesh(geometry, createAreaLightMaterial(intensity));
    light.position.set(pos[0], pos[1], pos[2]);
    light.scale.set(scale[0], scale[1], scale[2]);
    envScene.add(light);
  }

  return envScene;
}

export function setupLighting(
  scene: THREE.Scene,
  _profile: DeviceProfile,
  renderer: THREE.WebGLRenderer,
  opts: LightingOptions = {},
): void {
  // White studio backdrop (v4: the viewer reads as a clean white set). The
  // canvas/host can recolor this via Engine.setBackground for theming.
  scene.background = new THREE.Color(0xffffff);

  // Image-based lighting: the legacy room baked through PMREM (GENERATED_SIGMA
  // 0.04). This is model-viewer's actual default environment, not RoomEnvironment.
  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const envScene = buildLegacyEnvironmentScene();
  scene.environment = pmrem.fromScene(envScene, 0.04).texture;
  // Free the env-scene geometry/materials (one shared BoxGeometry + per-light mats).
  envScene.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else if (mat) mat.dispose();
  });
  pmrem.dispose();

  scene.environmentIntensity = opts.environmentIntensity ?? DEFAULT_ENV_INTENSITY;

  // The optional KEY light is added as a camera HEADLIGHT (see addCameraHeadlight),
  // not here — so it tracks the camera and the lit aspect stays constant while the
  // user rotates the sofa, instead of the highlight sliding across the world-fixed
  // surface.
}

/**
 * Soft KEY light layered on top of the IBL, parented to the CAMERA so it behaves
 * like a headlight: as the user orbits, the light rotates WITH the view, so the
 * lit face of the model stays consistent (the highlight doesn't slide around).
 * Default off (pure IBL); a product opts in via viewer.keyLightIntensity.
 *
 * Mechanism: the light + its target are children of the camera, so their world
 * direction is recomputed from the camera's matrix every render — no per-frame
 * code. The camera is added to the scene so the camera-parented light is part of
 * scene lighting (a camera in the graph is otherwise inert / not drawn).
 */
export function addCameraHeadlight(
  scene: THREE.Scene,
  camera: THREE.Camera,
  intensity: number,
): void {
  if (intensity <= 0) return;
  const key = new THREE.DirectionalLight(0xffffff, intensity);
  key.name = "neovaa-key";
  // Camera-relative: up + to the left + slightly behind the viewer (over the
  // shoulder), aimed in front of the camera → a flattering front-upper-left key
  // that stays put relative to the view as the model spins.
  key.position.set(-3, 4, 5);
  key.target.position.set(0, 0, -8);
  camera.add(key);
  camera.add(key.target);
  if (!camera.parent) scene.add(camera);
}

export interface ContactShadowOptions {
  /** Shadow strength 0-1 (the ground-plane opacity). Per-product shadowOpacity
   *  (Bangalow 0.75). Higher = darker. Default 0.5. */
  intensity?: number;
  /** Gaussian blur amount (in plane-space units) applied to the captured
   *  silhouette. Small = tight/crisp contact, large = diffuse. Default tuned
   *  for a slight, tight, soft-but-present contact shadow. */
  blur?: number;
}

/**
 * Footprint-projected soft contact shadow (three.js `webgl_shadow_contact`
 * technique). NOT a centred radial blob — the model is rendered top-down as a
 * flat dark silhouette into a render-target, that RT is gaussian-blurred, and
 * the result is mapped onto a ground plane. The shadow contour therefore
 * follows the ACTUAL footprint of any shape/size (an L-shape gets an L-shaped
 * shadow, not a centred ellipse).
 *
 * Geometry/alignment is the verbatim `webgl_shadow_contact` layout so the RT↔
 * plane mapping is neither mirrored nor rotated:
 *   - a local `group` is positioned at the footprint centre on the floor;
 *   - the ground PlaneGeometry is rotated `-PI/2` about X (faces +Y, lies flat),
 *     with its default UVs — sampling rt.texture maps 1:1 to the footprint;
 *   - the OrthographicCamera is a child of the group, also rotated `-PI/2`
 *     about X (so it looks straight DOWN, -Y), with matching left/right/top/
 *     bottom = the plane half-extents. Identical orientation of plane + camera
 *     is what guarantees no mirror/rotation.
 *
 * Lifecycle: `fit(box)` after each rebuild (rebuilds RT/plane/camera to the new
 * footprint). `render()` per refresh frame (captures + blurs the silhouette).
 * `dispose()` on teardown.
 */
export class ContactShadow {
  readonly group: THREE.Group;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private intensity: number;
  private blurAmount: number;

  // Per-fit resources (rebuilt each fit, disposed in teardown).
  private rt: THREE.WebGLRenderTarget | null = null;
  private rtBlur: THREE.WebGLRenderTarget | null = null;
  private plane: THREE.Mesh | null = null;
  private planeMat: THREE.MeshBasicMaterial | null = null;
  private blurPlane: THREE.Mesh | null = null;
  private shadowCamera: THREE.OrthographicCamera | null = null;
  private planeW = 1;
  private planeD = 1;

  // Shared (one-time) resources.
  private depthMaterial: THREE.MeshBasicMaterial;
  private horizontalBlurMaterial: THREE.ShaderMaterial;
  private verticalBlurMaterial: THREE.ShaderMaterial;

  private readonly RT_SIZE = 256;

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, opts: ContactShadowOptions = {}) {
    this.renderer = renderer;
    this.scene = scene;
    this.intensity = opts.intensity ?? 0.5;
    // Blur radius in RT texels (per H/V pass). ~2.4 reads as a slight, tight,
    // soft contact shadow at 256² — small enough to keep the footprint contour
    // legible (an L stays an L), large enough not to be a hard cutout.
    this.blurAmount = opts.blur ?? 2.8;

    this.group = new THREE.Group();
    this.group.name = "contact-shadow";
    scene.add(this.group);

    // Flat dark override material: every mesh renders as solid darkness so the
    // top-down pass produces a clean silhouette on a transparent background.
    // Darkness scalar (<1) keeps the shadow from being pitch-black before blur.
    this.depthMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
    this.depthMaterial.depthTest = true;
    this.depthMaterial.depthWrite = true;

    // Inline blur materials (reused vertically/horizontally per render).
    // DoubleSide so the blur quad is never back-face culled by the top-down
    // ortho camera regardless of the quad's normal orientation.
    this.horizontalBlurMaterial = new THREE.ShaderMaterial(HorizontalBlurShader);
    this.horizontalBlurMaterial.depthTest = false;
    this.horizontalBlurMaterial.side = THREE.DoubleSide;
    this.verticalBlurMaterial = new THREE.ShaderMaterial(VerticalBlurShader);
    this.verticalBlurMaterial.depthTest = false;
    this.verticalBlurMaterial.side = THREE.DoubleSide;
  }

  /** Size + position the shadow rig to a model bounding box. Call after rebuild. */
  fit(box: THREE.Box3): void {
    this.teardownFit();
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    // TIGHT footprint: only a small margin past the product edge so the soft
    // blur fades just outside the silhouette (Alex: slight, tight, not spread).
    this.planeW = Math.max(size.x, 0.2) * 1.32;
    this.planeD = Math.max(size.z, 0.2) * 1.32;
    const modelHeight = Math.max(size.y, 0.2);

    // Render targets: the silhouette capture + a ping-pong buffer for blur.
    this.rt = new THREE.WebGLRenderTarget(this.RT_SIZE, this.RT_SIZE);
    this.rt.texture.generateMipmaps = false;
    this.rtBlur = new THREE.WebGLRenderTarget(this.RT_SIZE, this.RT_SIZE);
    this.rtBlur.texture.generateMipmaps = false;

    // Ground plane that DISPLAYS the shadow. Verbatim webgl_shadow_contact
    // layout: rotateX(+PI/2) so it lies flat, default UVs.
    const planeGeo = new THREE.PlaneGeometry(this.planeW, this.planeD).rotateX(Math.PI / 2);
    this.planeMat = new THREE.MeshBasicMaterial({
      map: this.rt.texture,
      transparent: true,
      opacity: this.intensity,
      depthWrite: false,
      toneMapped: false,
    });
    this.plane = new THREE.Mesh(planeGeo, this.planeMat);
    this.plane.renderOrder = 2;
    // CRITICAL alignment fix (webgl_shadow_contact): the top-down RT render
    // flips V, and rotateX(+PI/2) leaves the plane facing DOWN. scale.y = -1
    // flips the plane to face UP AND un-mirrors the sampled texture so the
    // silhouette lands exactly over the real footprint (no mirror, no rotation).
    this.plane.scale.y = -1;

    // Hidden blur plane (a flat quad we render the blur passes through). It must
    // NOT inherit the scale.y flip — it's only used as a fullscreen quad for the
    // ortho blur camera, so a fresh flat plane keeps the blur UVs upright.
    this.blurPlane = new THREE.Mesh(planeGeo.clone());
    this.blurPlane.visible = false;

    // Top-down orthographic camera covering exactly the footprint extents.
    // near=0 at the top of the model, far reaching the floor below.
    this.shadowCamera = new THREE.OrthographicCamera(
      -this.planeW / 2,
      this.planeW / 2,
      this.planeD / 2,
      -this.planeD / 2,
      0,
      modelHeight + 0.1,
    );
    // A default camera looks down its local -Z. rotateX(-PI/2) maps -Z → -Y, so
    // the camera looks straight DOWN at the floor below it. (+PI/2 would look UP,
    // away from the model — the previous "RT empty" bug.)
    this.shadowCamera.rotation.x = -Math.PI / 2;
    this.group.add(this.shadowCamera);
    this.group.add(this.plane);
    this.group.add(this.blurPlane);

    // Position the rig: centre on the footprint at the floor. The plane sits a
    // hair above the floor to avoid z-fighting; the camera sits at model height.
    this.group.position.set(center.x, box.min.y, center.z);
    this.plane.position.y = 0.002;
    this.shadowCamera.position.y = modelHeight + 0.05;
    this.group.updateMatrixWorld(true);
  }

  /** Capture the top-down silhouette, blur it, leave it on the ground plane. */
  render(): void {
    if (!this.rt || !this.rtBlur || !this.plane || !this.shadowCamera || !this.blurPlane) return;
    const renderer = this.renderer;
    const scene = this.scene;

    // Hide the display plane so it isn't captured in its own pass.
    const planeWasVisible = this.plane.visible;
    this.plane.visible = false;

    // Save scene state we are about to override.
    const prevBackground = scene.background;
    const prevEnvIntensity = scene.environmentIntensity;
    const prevOverride = scene.overrideMaterial;
    const prevClearColor = new THREE.Color();
    renderer.getClearColor(prevClearColor);
    const prevClearAlpha = renderer.getClearAlpha();
    const prevRenderTarget = renderer.getRenderTarget();

    // Flat dark silhouette on a transparent background.
    scene.background = null;
    scene.environmentIntensity = 0;
    scene.overrideMaterial = this.depthMaterial;
    renderer.setClearColor(0x000000, 0); // transparent clear

    renderer.setRenderTarget(this.rt);
    renderer.clear();
    renderer.render(scene, this.shadowCamera);

    // Restore the override + scene state immediately (blur passes don't need it).
    scene.overrideMaterial = prevOverride;
    scene.background = prevBackground;
    scene.environmentIntensity = prevEnvIntensity;

    // Gaussian blur the silhouette twice (H then V, ×2) so the contact reads
    // soft but still hugs the footprint contour. Blur radius in RT texels.
    const b = this.blurAmount / this.RT_SIZE;
    this.blurPass(this.rt, this.rtBlur, b, this.horizontalBlurMaterial, "h");
    this.blurPass(this.rtBlur, this.rt, b, this.verticalBlurMaterial, "v");
    // Second, lighter pass smooths residual texel banding without bloating size.
    const b2 = (this.blurAmount * 0.5) / this.RT_SIZE;
    this.blurPass(this.rt, this.rtBlur, b2, this.horizontalBlurMaterial, "h");
    this.blurPass(this.rtBlur, this.rt, b2, this.verticalBlurMaterial, "v");

    // Restore renderer state.
    renderer.setRenderTarget(prevRenderTarget);
    renderer.setClearColor(prevClearColor, prevClearAlpha);

    this.plane.visible = planeWasVisible;
  }

  /** One blur pass: sample `from`, write blurred result into `to`. */
  private blurPass(
    from: THREE.WebGLRenderTarget,
    to: THREE.WebGLRenderTarget,
    amount: number,
    mat: THREE.ShaderMaterial,
    axis: "h" | "v",
  ): void {
    if (!this.blurPlane || !this.shadowCamera) return;
    mat.uniforms.tDiffuse.value = from.texture;
    if (axis === "h") mat.uniforms.h.value = amount;
    else mat.uniforms.v.value = amount;
    this.blurPlane.material = mat;
    this.blurPlane.visible = true;

    this.renderer.setRenderTarget(to);
    this.renderer.clear();
    this.renderer.render(this.blurPlane, this.shadowCamera);

    this.blurPlane.visible = false;
  }

  private teardownFit(): void {
    if (this.plane) {
      this.group.remove(this.plane);
      this.plane.geometry.dispose();
      this.plane = null;
    }
    if (this.planeMat) {
      this.planeMat.dispose();
      this.planeMat = null;
    }
    if (this.blurPlane) {
      this.group.remove(this.blurPlane);
      this.blurPlane.geometry.dispose();
      this.blurPlane = null;
    }
    if (this.shadowCamera) {
      this.group.remove(this.shadowCamera);
      this.shadowCamera = null;
    }
    if (this.rt) {
      this.rt.dispose();
      this.rt = null;
    }
    if (this.rtBlur) {
      this.rtBlur.dispose();
      this.rtBlur = null;
    }
  }

  dispose(): void {
    this.teardownFit();
    this.depthMaterial.dispose();
    this.horizontalBlurMaterial.dispose();
    this.verticalBlurMaterial.dispose();
    this.group.removeFromParent();
  }
}
